import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { MessageSquare, FileText, Users, UserCog, TrendingUp, Bell } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";

interface NavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  requiresAdmin?: boolean;
}

const navItems: NavItem[] = [
  {
    to: "/",
    icon: MessageSquare,
    label: "Chat",
  },
  {
    to: "/admin/documents",
    icon: FileText,
    label: "Docs",
  },
  {
    to: "/notifications",
    icon: Bell,
    label: "Alerts",
  },
  {
    to: "/teams",
    icon: Users,
    label: "Teams",
  },
  {
    to: "/dashboard/team-metrics",
    icon: TrendingUp,
    label: "Metrics",
    requiresAdmin: true,
  },
  {
    to: "/admin/users",
    icon: UserCog,
    label: "Admin",
    requiresAdmin: true,
  },
];

interface MobileBottomNavProps {
  isAdmin?: boolean;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ isAdmin = false }) => {
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);

  // Filter nav items based on admin status
  const visibleItems = navItems.filter(item => !item.requiresAdmin || isAdmin);

  useEffect(() => {
    fetchUnreadCount();

    // Subscribe to realtime notifications
    const channel = supabase
      .channel('mobile-notifications-channel')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
        },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchUnreadCount = async () => {
    const { data } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .is('read_at', null);

    setUnreadCount(data?.length || 0);
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card border-t border-border shadow-elevated lg:hidden">
      <div className="flex items-center justify-around h-16">
        {visibleItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.to;

          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={cn(
                "flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors touch-manipulation relative",
                "hover:bg-muted/50 active:bg-muted",
                isActive && "text-primary"
              )}
            >
              <div className="relative">
                <Icon 
                  className={cn(
                    "h-5 w-5 transition-all",
                    isActive && "scale-110"
                  )} 
                />
                {item.label === "Alerts" && unreadCount > 0 && (
                  <Badge
                    variant="destructive"
                    className="absolute -top-2 -right-2 h-4 w-4 flex items-center justify-center p-0 text-[10px]"
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </Badge>
                )}
              </div>
              <span className={cn(
                "text-xs font-medium transition-all",
                isActive && "font-semibold"
              )}>
                {item.label}
              </span>
              
              {/* Active indicator */}
              {isActive && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary animate-scale-in" />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};
